import { db } from "../../helpers/firebase";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

const COLLECTION_NAME = "leases";

/**
 * Lease Model - Handles all lease-related operations
 *
 * Data Structure:
 * {
 *   id: string,
 *   ownerId: string,           // Property owner/landlord ID
 *   unitId: string,           // Reference to unit
 *   tenantId: string,          // Reference to tenant
 *   leaseNumber: string,       // Unique lease identifier
 *   startDate: timestamp,      // Lease start date
 *   endDate: timestamp,        // Lease end date
 *   monthlyRent: number,       // Monthly rent amount
 *   securityDeposit: number,   // Security deposit amount
 *   status: string,            // 'draft', 'pending_signature', 'active', 'expired', 'terminated'
 *   pdfUrl: string,           // URL to uploaded PDF
 *   signatureFields: [        // Array of signature field definitions
 *     {
 *       id: string,
 *       x: number,            // X coordinate on PDF
 *       y: number,            // Y coordinate on PDF
 *       page: number,         // Page number
 *       signerType: string,   // 'tenant', 'landlord', 'guarantor'
 *       signerId: string,     // ID of the person who needs to sign
 *       required: boolean,
 *       signed: boolean,
 *       signedAt: timestamp,
 *       signatureData: string // Base64 signature data
 *     }
 *   ],
 *   terms: {                  // Lease terms and conditions
 *     utilities: string,      // Who pays utilities
 *     pets: boolean,          // Pets allowed
 *     parking: string,        // Parking information
 *     lateFees: number,       // Late fee amount
 *     gracePeriod: number    // Grace period in days
 *   },
 *   createdAt: timestamp,
 *   updatedAt: timestamp,
 *   signedAt: timestamp,      // When all signatures are complete
 *   terminatedAt: timestamp  // When lease was terminated
 * }
 */

class LeaseModel {
  /**
   * Create a new lease
   */
  static async create(leaseData) {
    try {
      const lease = {
        ...leaseData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: "draft",
        signatureFields: leaseData.signatureFields || [],
        terms: leaseData.terms || {},
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), lease);

      // Update the document with the generated ID
      await updateDoc(docRef, { id: docRef.id });

      return { id: docRef.id, ...lease };
    } catch (error) {
      console.error("Error creating lease:", error);
      throw new Error("Failed to create lease");
    }
  }

  /**
   * Get a lease by ID
   */
  static async getById(leaseId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, leaseId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error("Lease not found");
      }
    } catch (error) {
      console.error("Error getting lease:", error);
      throw new Error("Failed to get lease");
    }
  }

  /**
   * Get all leases for a property owner
   */
  static async getByOwner(ownerId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("ownerId", "==", ownerId),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const leases = [];

      querySnapshot.forEach((doc) => {
        leases.push({ id: doc.id, ...doc.data() });
      });

      return leases;
    } catch (error) {
      console.error("Error getting leases by owner:", error);
      throw new Error("Failed to get leases");
    }
  }

  /**
   * Get leases for a specific unit
   */
  static async getByUnit(unitId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("unitId", "==", unitId),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const leases = [];

      querySnapshot.forEach((doc) => {
        leases.push({ id: doc.id, ...doc.data() });
      });

      return leases;
    } catch (error) {
      console.error("Error getting leases by unit:", error);
      throw new Error("Failed to get unit leases");
    }
  }

  /**
   * Get leases for a specific tenant
   */
  static async getByTenant(tenantId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("tenantId", "==", tenantId),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const leases = [];

      querySnapshot.forEach((doc) => {
        leases.push({ id: doc.id, ...doc.data() });
      });

      return leases;
    } catch (error) {
      console.error("Error getting leases by tenant:", error);
      throw new Error("Failed to get tenant leases");
    }
  }

  /**
   * Update a lease
   */
  static async update(leaseId, updateData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, leaseId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      return { id: leaseId, ...updateData };
    } catch (error) {
      console.error("Error updating lease:", error);
      throw new Error("Failed to update lease");
    }
  }

  /**
   * Delete a lease
   */
  static async delete(leaseId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, leaseId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error("Error deleting lease:", error);
      throw new Error("Failed to delete lease");
    }
  }

  /**
   * Upload PDF and update lease
   */
  static async uploadPDF(leaseId, pdfFile) {
    try {
      // TODO: Implement file upload to Firebase Storage
      // For now, return a mock URL
      const pdfUrl = `https://storage.googleapis.com/landlordops-leases/${leaseId}/${pdfFile.name}`;

      await this.update(leaseId, {
        pdfUrl,
        status: "pending_signature",
      });

      return pdfUrl;
    } catch (error) {
      console.error("Error uploading PDF:", error);
      throw new Error("Failed to upload PDF");
    }
  }

  /**
   * Add signature field to lease
   */
  static async addSignatureField(leaseId, signatureField) {
    try {
      const docRef = doc(db, COLLECTION_NAME, leaseId);
      await updateDoc(docRef, {
        signatureFields: arrayUnion(signatureField),
        updatedAt: serverTimestamp(),
      });

      return signatureField;
    } catch (error) {
      console.error("Error adding signature field:", error);
      throw new Error("Failed to add signature field");
    }
  }

  /**
   * Sign a specific field
   */
  static async signField(leaseId, fieldId, signatureData, signerId) {
    try {
      const lease = await this.getById(leaseId);
      const updatedFields = lease.signatureFields.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            signed: true,
            signedAt: serverTimestamp(),
            signatureData,
            signerId,
          };
        }
        return field;
      });

      // Check if all required fields are signed
      const allSigned = updatedFields.every(
        (field) => !field.required || field.signed
      );
      const newStatus = allSigned ? "active" : "pending_signature";

      await this.update(leaseId, {
        signatureFields: updatedFields,
        status: newStatus,
        signedAt: allSigned ? serverTimestamp() : null,
      });

      return { signatureFields: updatedFields, status: newStatus };
    } catch (error) {
      console.error("Error signing field:", error);
      throw new Error("Failed to sign field");
    }
  }

  /**
   * Get leases expiring soon
   */
  static async getExpiringSoon(ownerId, days = 60) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() + days);

      const q = query(
        collection(db, COLLECTION_NAME),
        where("ownerId", "==", ownerId),
        where("endDate", "<=", cutoffDate),
        where("status", "==", "active"),
        orderBy("endDate", "asc")
      );

      const querySnapshot = await getDocs(q);
      const leases = [];

      querySnapshot.forEach((doc) => {
        leases.push({ id: doc.id, ...doc.data() });
      });

      return leases;
    } catch (error) {
      console.error("Error getting expiring leases:", error);
      throw new Error("Failed to get expiring leases");
    }
  }

  /**
   * Terminate a lease
   */
  static async terminate(leaseId, reason) {
    try {
      await this.update(leaseId, {
        status: "terminated",
        terminatedAt: serverTimestamp(),
        terminationReason: reason,
      });

      return true;
    } catch (error) {
      console.error("Error terminating lease:", error);
      throw new Error("Failed to terminate lease");
    }
  }

  /**
   * Renew a lease
   */
  static async renew(leaseId, newEndDate, newMonthlyRent) {
    try {
      const lease = await this.getById(leaseId);

      // Create a new lease based on the current one
      const newLeaseData = {
        ...lease,
        startDate: lease.endDate,
        endDate: newEndDate,
        monthlyRent: newMonthlyRent || lease.monthlyRent,
        status: "draft",
        signatureFields: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      delete newLeaseData.id;
      delete newLeaseData.signedAt;
      delete newLeaseData.terminatedAt;

      const newLease = await this.create(newLeaseData);

      // Update the old lease status
      await this.update(leaseId, { status: "renewed" });

      return newLease;
    } catch (error) {
      console.error("Error renewing lease:", error);
      throw new Error("Failed to renew lease");
    }
  }
}

export default LeaseModel;
